import React, {useState, useCallback, useRef, useEffect} from 'react';
import {intRegex, emailRegex, handleAllowOnlyNumbers} from '../app/utils';
import {IntelliSearchSelect} from './IntelliSearchSelect';

// MUI
import {MuiChipsInput} from 'mui-chips-input';
import Chip from '@mui/material/Chip';
import {styled} from '@mui/material/styles';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Collapse,
  CircularProgress,
  TableRow,
  TableCell,
  Switch,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Tooltip,
  Paper,
  Box,
  Divider,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/** MUI Tooltip defaults so info icons work on touch (mobile) as well as hover. */
const settingsInfoTooltipProps = {
  enterTouchDelay: 0,
  leaveTouchDelay: 5000,
  arrow: true,
  placement: 'top',
  PopperProps: {
    popperOptions: {
      modifiers: [
        {
          name: 'preventOverflow',
          enabled: true,
          boundary: 'viewport',
          padding: 8,
        },
        {
          name: 'flip',
          enabled: true,
        },
      ],
    },
  },
  componentsProps: {
    tooltip: {
      sx: {
        maxWidth: 'min(360px, calc(100vw - 32px))',
        whiteSpace: 'normal',
      },
    },
  },
};

export const NoBorderTableRow = styled(TableRow)(({theme}) => {
  return {
    '&.MuiTableRow-root': {
      borderBottom: 'none', // Remove bottom border
    },
    '& .MuiTableCell-root': {
      borderBottom: 'none', // Remove border from cells within the row
    },
  };
});
export const NoBorderTableCell = styled(TableCell)`
  borderBottom: 'none' '& td': {
      borderBottom: 'none'
    }
`;

export function CollapsableSection(props) {
  // Supports boolean collapse state or a flat map like {advanced: false}.
  const isBooleanCollapse = typeof props.collapseOpen === 'boolean';
  const open = isBooleanCollapse ? props.collapseOpen : props.collapseOpen[props.section];
  return (
    <div>
      <h5 className='mt-2' style={{color: 'black'}}>
        {props.title}
        {props.viewOnly && <span style={{fontWeight: 'bold'}}> - View Only</span>}
        <span
          className='ml-2 hoverPls'
          onClick={() => {
            props.setCollapseOpen((values) => {
              if (typeof values === 'boolean') {
                return !values;
              }
              return {...values, [props.section]: !values[props.section]};
            });
          }}
        >
          {open ? <ExpandLess /> : <ExpandMore />}
        </span>
      </h5>
      <Collapse in={open} timeout='auto' unmountOnExit>
        {props.children}
      </Collapse>
    </div>
  );
}

export function ModalSubmitting(props) {
  return (
    <div style={{display: 'flex', flexDirection: 'column', textAlign: 'center'}}>
      <CircularProgress className='mt-4 mx-auto d-block' size={80} />
      <span className='mt-3'>Submitting ...</span>
    </div>
  );
}

export function ViewOnlyText(props) {
  return (
    <h5 className='mt-2' style={{color: 'blue'}}>
      <i>View Only</i>
    </h5>
  );
}

function SettingsSwitchInner(props) {
  const isSubToggle = (props.parentSetting != null && props.parentSetting !== '') || Boolean(props.isSubToggle);
  const parentEnabled =
    props.parentSetting == null ||
    props.parentSetting === '' ||
    (props.formValues && !!props.formValues[props.parentSetting]);
  const isDisabled = props.disabled || !parentEnabled;
  const isParentToggle = props.hasSubToggle && !isSubToggle;
  const isChecked =
    props.checked !== undefined ? Boolean(props.checked) : Boolean(props.formValues && props.formValues[props.setting]);

  return (
    <React.Fragment>
      <TableRow>
        <TableCell
          sx={{
            ...(props.children || isParentToggle ? {borderBottom: 'none'} : {}),
            ...(isSubToggle ? {pl: '48px'} : {}),
          }}
        >
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.25}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
              {props.label}
              {props.infoTooltip ? (
                <Tooltip title={props.infoTooltip} {...settingsInfoTooltipProps}>
                  <IconButton size='small' aria-label='More information' sx={{p: 0.25}}>
                    <InfoOutlinedIcon fontSize='small' color='action' />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
            {props.sublabel && <Box sx={{fontSize: '0.75rem', color: 'text.secondary'}}>{props.sublabel}</Box>}
          </Box>
        </TableCell>
        <TableCell sx={props.children || isParentToggle ? {borderBottom: 'none'} : {}}>
          <Box className='justify-content-center justify-content-md-start' sx={{display: 'flex', alignItems: 'center'}}>
            <Switch
              disabled={isDisabled}
              name={props.setting || props.name}
              checked={isChecked}
              onChange={props.handleChange || props.onChange}
              color={props.color || 'primary'}
            />
            {props.warningTooltip ? (
              <Tooltip title={props.warningTooltip} {...settingsInfoTooltipProps}>
                <IconButton size='small' aria-label='Warning' sx={{p: 0.25, ml: 0.5}} onClick={props.onWarningClick}>
                  <WarningAmberIcon fontSize='small' color='warning' />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        </TableCell>
      </TableRow>
      {props.children && (
        <TableRow>
          <TableCell colSpan={2} sx={{pt: 0, pb: 1, ...(isSubToggle ? {pl: '48px'} : {})}}>
            {props.children}
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}

// Compares only the specific formValues keys this component reads, so a sibling field changing
// elsewhere on a shared form object doesn't force this row to re-render.
function settingsSwitchPropsAreEqual(prev, next) {
  return (
    prev.setting === next.setting &&
    prev.name === next.name &&
    prev.parentSetting === next.parentSetting &&
    prev.checked === next.checked &&
    prev.formValues?.[prev.setting] === next.formValues?.[next.setting] &&
    prev.formValues?.[prev.parentSetting] === next.formValues?.[next.parentSetting] &&
    prev.disabled === next.disabled &&
    prev.isSubToggle === next.isSubToggle &&
    prev.hasSubToggle === next.hasSubToggle &&
    prev.children === next.children &&
    prev.label === next.label &&
    prev.sublabel === next.sublabel &&
    prev.infoTooltip === next.infoTooltip &&
    prev.warningTooltip === next.warningTooltip &&
    prev.onWarningClick === next.onWarningClick &&
    prev.color === next.color &&
    prev.handleChange === next.handleChange &&
    prev.onChange === next.onChange
  );
}

export const SettingsSwitch = React.memo(SettingsSwitchInner, settingsSwitchPropsAreEqual);

export function TargetText(props) {
  let value = '';
  try {
    value = props.formValues[props.outerTarget][props.innerTarget][props.targetName];
  } catch (error) {
    value = '';
  }

  let type = props.type ?? 'text';
  if (type == 'number') {
    type = 'text';
  }

  return (
    <TableRow>
      {props.showLabel && <TableCell>{props.label}</TableCell>}
      <TableCell>
        <TextField
          disabled={props.disabled}
          size='small'
          fullWidth
          type={type}
          placeholder={props.placeholder}
          InputProps={{inputProps: props.inputProps}}
          name={props.innerTarget}
          value={value}
          onChange={props.handleChange}
          onKeyDown={props.type == 'number' ? handleAllowOnlyNumbers : null}
        />
      </TableCell>
    </TableRow>
  );
}

function SettingsTextInner(props) {
  let type = props.type ?? 'text';
  if (type == 'number') {
    type = 'text';
  }
  const autoComplete = props.autoComplete ?? (props.type === 'password' ? 'new-password' : 'off');
  const inputProps = {...(props.inputProps || {}), autoComplete};

  return (
    <TableRow>
      <TableCell>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          {props.label}
          {props.infoTooltip ? (
            <Tooltip title={props.infoTooltip} {...settingsInfoTooltipProps}>
              <IconButton size='small' aria-label='More information' sx={{p: 0.25}}>
                <InfoOutlinedIcon fontSize='small' color='action' />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </TableCell>
      <TableCell>
        <TextField
          disabled={props.disabled}
          size='small'
          fullWidth
          label={props.textBoxLabel}
          type={type}
          placeholder={props.placeholder}
          InputProps={{inputProps}}
          name={props.setting}
          value={props.formValues[props.setting]}
          onChange={props.handleChange}
          onKeyDown={props.type == 'number' ? handleAllowOnlyNumbers : null}
        />
        {props.inputFooter}
      </TableCell>
    </TableRow>
  );
}

// Compares only this row's own formValues key, so typing in a sibling field on the same shared
// form object doesn't force this row to re-render.
function settingsTextPropsAreEqual(prev, next) {
  return (
    prev.setting === next.setting &&
    prev.formValues?.[prev.setting] === next.formValues?.[next.setting] &&
    prev.disabled === next.disabled &&
    prev.handleChange === next.handleChange &&
    prev.label === next.label &&
    prev.type === next.type &&
    prev.autoComplete === next.autoComplete &&
    prev.placeholder === next.placeholder &&
    prev.textBoxLabel === next.textBoxLabel &&
    prev.infoTooltip === next.infoTooltip &&
    prev.inputFooter === next.inputFooter &&
    prev.inputProps === next.inputProps
  );
}

export const SettingsText = React.memo(SettingsTextInner, settingsTextPropsAreEqual);

export function SettingsTextAndToggle(props) {
  let type = props.type ?? 'text';
  if (type == 'number') {
    type = 'text';
  }

  return (
    <TableRow>
      <TableCell>{props.label}</TableCell>
      <TableCell>
        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1}}>
          <Switch
            disabled={props.disabled}
            name={props.toggleSetting}
            checked={props.formValues[props.toggleSetting]}
            onChange={props.handleChange}
          />
          <TextField
            disabled={props.disabled || !props.formValues[props.toggleSetting]}
            size='small'
            fullWidth
            type={type}
            placeholder={props.placeholder}
            InputProps={{inputProps: props.inputProps}}
            label={props.textBoxLabel}
            name={props.setting}
            value={props.formValues[props.setting]}
            onChange={props.handleChange}
            onKeyDown={props.type == 'number' ? handleAllowOnlyNumbers : null}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
}

function SettingsSelectInner(props) {
  return (
    <TableRow>
      <TableCell>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          {props.label}
          {props.infoTooltip ? (
            <Tooltip title={props.infoTooltip} {...settingsInfoTooltipProps}>
              <IconButton size='small' aria-label='More information' sx={{p: 0.25}}>
                <InfoOutlinedIcon fontSize='small' color='action' />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </TableCell>
      <TableCell>
        <Select
          multiple={props.multiple}
          disabled={props.disabled}
          size='small'
          fullWidth
          name={props.setting}
          value={props.formValues[props.setting]}
          onChange={props.handleChange}
        >
          {props.options.map((option, idx) => {
            return (
              <MenuItem key={idx} value={option.value}>
                {option.label}
              </MenuItem>
            );
          })}
        </Select>
      </TableCell>
    </TableRow>
  );
}

// Compares only this row's own formValues key, so typing in a sibling field on the same shared
// form object doesn't force this row to re-render.
function settingsSelectPropsAreEqual(prev, next) {
  return (
    prev.setting === next.setting &&
    prev.formValues?.[prev.setting] === next.formValues?.[next.setting] &&
    prev.multiple === next.multiple &&
    prev.disabled === next.disabled &&
    prev.handleChange === next.handleChange &&
    prev.options === next.options &&
    prev.label === next.label &&
    prev.infoTooltip === next.infoTooltip
  );
}

export const SettingsSelect = React.memo(SettingsSelectInner, settingsSelectPropsAreEqual);

function SettingsSearchSelectInner(props) {
  return (
    <TableRow>
      <TableCell width='30%'>{props.label}</TableCell>
      <TableCell>
        <IntelliSearchSelect
          multiple={props.multiple}
          disabled={props.disabled}
          name={props.setting}
          value={props.formValues[props.setting]}
          onChange={props.handleChange}
          options={props.options}
          allNoneEnabled
          error={props.error}
          helperText={props.helperText}
          returnFullEvent
          optionDisplayLimit={1}
          search={true}
          placeholder={props.placeholder}
        />
      </TableCell>
    </TableRow>
  );
}

// Compares only this row's own formValues key, so typing in a sibling field on the same shared
// form object doesn't force this row to re-render.
function settingsSearchSelectPropsAreEqual(prev, next) {
  return (
    prev.setting === next.setting &&
    prev.formValues?.[prev.setting] === next.formValues?.[next.setting] &&
    prev.multiple === next.multiple &&
    prev.disabled === next.disabled &&
    prev.handleChange === next.handleChange &&
    prev.options === next.options &&
    prev.error === next.error &&
    prev.helperText === next.helperText &&
    prev.placeholder === next.placeholder &&
    prev.label === next.label
  );
}

export const SettingsSearchSelect = React.memo(SettingsSearchSelectInner, settingsSearchSelectPropsAreEqual);

/**
 * Outlined name chips matching Settings Assets vehicle label chips.
 * Unmatched names render as error chips with a not-found tooltip.
 *
 * @param {Object} props Component props
 * @param {Array<string>} [props.names] Display names when items is omitted
 * @param {Array<Object>} [props.items] Chip items with key, label, optional color and tooltip
 * @param {Array<string>} [props.unmatchedNames] Names that did not resolve (shown as error chips)
 * @param {string} [props.unmatchedColumnLabel] Column display name used in unmatched tooltips
 * @param {string} [props.emptyLabel] Text shown when there are no chips
 * @param {string} [props.className] Optional wrapper class name
 * @return {JSX.Element|null} Rendered name chips or empty placeholder
 */
export function SettingsNameChips({names, items, unmatchedNames, unmatchedColumnLabel, emptyLabel, className}) {
  const chipItems =
    items ||
    (names || []).map((name, index) => {
      return {
        key: `${name}-${index}`,
        label: name,
        color: 'warning',
      };
    });

  const errorItems = (unmatchedNames || []).map((name, index) => {
    const tooltipPrefix = unmatchedColumnLabel ? `${unmatchedColumnLabel}: ` : '';
    return {
      key: `unmatched-${name}-${index}`,
      label: name,
      color: 'error',
      tooltip: `${tooltipPrefix}${name} is not found`,
    };
  });

  const allItems = [...chipItems, ...errorItems];

  if (allItems.length === 0) {
    return emptyLabel ? <span className='text-muted'>{emptyLabel}</span> : null;
  }

  return (
    <Box
      component='span'
      className={className}
      sx={{
        display: 'inline-flex',
        flexWrap: 'wrap',
        gap: 0.5,
        alignItems: 'center',
        verticalAlign: 'middle',
      }}
    >
      {allItems.map((item) => {
        const chip = (
          <Chip size='small' variant='outlined' className='mr-1' label={item.label} color={item.color || 'warning'} />
        );
        if (!item.tooltip) {
          return <React.Fragment key={item.key}>{chip}</React.Fragment>;
        }
        return (
          <Tooltip key={item.key} title={item.tooltip} arrow>
            <span>{chip}</span>
          </Tooltip>
        );
      })}
    </Box>
  );
}

export function SettingsChipInput(props) {
  // Variables for text inputType
  let type = 'text';
  let varType = 'string';
  let regex = null;
  let errorMsg = '';

  if (props.inputType == 'integer') {
    // Variables for integer inputType
    type = 'number';
    varType = 'int';
    regex = intRegex;
    errorMsg = 'The value must be an integer';
  } else if (props.inputType == 'email') {
    // Variables for email inputType
    type = 'email';
    varType = 'string';
    regex = emailRegex;
    errorMsg = 'Please enter an email address';
  }

  return (
    <TableRow>
      <TableCell>{props.label}</TableCell>
      <TableCell>
        <MuiChipsInput
          disabled={props.disabled}
          size='small'
          type={type}
          fullWidth
          hideClearAll
          disableDeleteOnBackspace
          name={props.setting}
          value={props.formValues[props.setting]}
          onChange={(value) => {
            let tempValues = [...value];
            if (varType == 'int') {
              tempValues = tempValues.map((val) => {
                return parseInt(val);
              });
            }

            // Create mock event to pass to actual event handler
            const mockEvent = {
              target: {
                name: props.setting,
                value: tempValues,
              },
            };
            props.handleChange(mockEvent);
          }}
          validate={(newValue) => {
            if (regex === null) {
              return true;
            }
            return {
              isError: !regex.test(newValue),
              textError: errorMsg,
            };
          }}
        />
      </TableCell>
    </TableRow>
  );
}

export function GeneralSearchSelect(props) {
  const [hoverAll, setHoverAll] = useState(false);
  const [hoverNone, setHoverNone] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [prevInputValue, setPrevInputValue] = useState('');
  const [showMore, setShowMore] = useState(false);
  const [hasRestrictedOption, setHasRestrictedOption] = useState(false);

  useEffect(() => {
    // Check if any selected value is NOT an option (inaccessible by the user)
    if (props.options && props.value && props.multiple) {
      let restrictedOptionsFound = false;
      const optionValues = props.options.map((opt) => {
        return opt.value;
      });
      for (let i = 0; i < props.value.length; i++) {
        if (!optionValues.includes(props.value[i])) {
          restrictedOptionsFound = true;
          break;
        }
      }
      setHasRestrictedOption(restrictedOptionsFound);
    }
  }, [props.options, props.value]);

  function handleAll() {
    const filteredOptionValues = props.options
      .filter((item) => {
        return item.label.toLowerCase().includes(inputValue.toLowerCase()) && !item.disabled;
      })
      .map((opt) => {
        return opt.value;
      });

    const unionOptions = [...new Set([...filteredOptionValues, ...props.value])];
    const mockEvent = {
      target: {
        name: props.name,
        value: unionOptions,
      },
    };
    setInputValue('');
    setPrevInputValue('');
    props.handleChange(mockEvent);
  }

  function handleNone() {
    let resultOptions = [];
    const filteredOption = props.options;
    if (inputValue) {
      filteredOption.filter((item) => {
        return item.label.toLowerCase().includes(inputValue.toLowerCase());
      });
    }
    const filteredOptionValues = filteredOption.map((opt) => {
      return opt.value;
    });
    resultOptions = props.value.filter((value) => {
      return !filteredOptionValues.includes(value);
    });
    const mockEvent = {
      target: {
        name: props.name,
        value: resultOptions,
      },
    };
    setInputValue('');
    setPrevInputValue('');
    props.handleChange(mockEvent);
  }

  const handleAllRef = useRef(handleAll);
  const handleNoneRef = useRef(handleNone);

  useEffect(() => {
    handleAllRef.current = handleAll;
    handleNoneRef.current = handleNone;
  });

  return (
    <Autocomplete
      multiple={props.multiple}
      disabled={props.disabled}
      size='small'
      fullWidth
      limitTags={props.limitTags || 4}
      disableCloseOnSelect={props.multiple}
      onInputChange={(event, value, reason) => {
        if (event != null && reason == 'reset') {
          // On select, reset input to previous value; input clear on all/none button
          setInputValue(prevInputValue);
        }
      }}
      inputValue={inputValue}
      name={props.name}
      value={props.value}
      options={props.options}
      getOptionDisabled={(option) => {
        return option?.disabled == true;
      }}
      onClose={() => {
        setShowMore(false);
      }}
      onChange={(event, value, reason) => {
        if (reason == 'clear' && props.multiple) {
          handleNoneRef.current();
          return;
        }
        // Autocomplete will return option object, but initial values will be return just the value
        let valueAdjusted = '';
        if (Array.isArray(value)) {
          valueAdjusted = value.map((option) => {
            if (typeof option == 'object') {
              return option.value;
            } else {
              return option;
            }
          });
        } else if (value !== null) {
          if (typeof value == 'object') {
            valueAdjusted = value.value;
          } else {
            valueAdjusted = value;
          }
        }

        // Create mock event to pass to actual event handler
        const mockEvent = {
          target: {
            name: props.name,
            value: valueAdjusted,
          },
        };
        props.handleChange(mockEvent);
      }}
      onBlur={() => {
        setInputValue('');
        setPrevInputValue('');
      }}
      sx={{
        'span.MuiAutocomplete-tag': {
          display: 'contents',
        },
      }}
      getOptionLabel={(option) => {
        if (typeof option == 'object') {
          return option.label;
        } else {
          const optionObj = props.options.find((opt) => {
            return opt.value == option;
          });
          if (optionObj) {
            return optionObj.label;
          } else {
            return '';
          }
        }
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              // Display placeholder for options selected that are inaccessible by the user
              startAdornment: [
                ...(hasRestrictedOption
                  ? [
                      <Tooltip
                        key='restrictedOption'
                        title={`You may not have access to these ${
                          props?.entityName || 'Option'
                        }s, Please contact your organization or IntelliCulture Support for more information`}
                        placement='top'
                        arrow
                      >
                        <Chip size='small' label={`Other Restricted ${props?.entityName || 'Option'} Selected`} />
                      </Tooltip>,
                    ]
                  : []),
                ...(params.InputProps.startAdornment ? params.InputProps.startAdornment : []),
              ],
            }}
            onChange={(e) => {
              setInputValue(e.target.value);
              setPrevInputValue(e.target.value);
            }}
            placeholder='Search...'
            error={props.error}
            helperText={props.helperText}
          />
        );
      }}
      renderTags={(tagValue, getTagProps) => {
        const tags = [];
        const moreVehiclesName = [];
        let tagCounts = 0;
        tagValue.forEach((value, index) => {
          const {key, ...tagProps} = getTagProps({index});
          const targetOption = props.options.find((option) => {
            return option.value == value;
          });
          if (targetOption) {
            const displayIndividualTag = tagCounts < (props.limitTags || 4) || showMore;
            if (displayIndividualTag) {
              tags.push(
                <Chip
                  size='small'
                  key={key}
                  label={targetOption.label}
                  {...tagProps}
                  disabled={targetOption.disabled}
                />
              );
            } else {
              tags.push(null);
              moreVehiclesName.push(targetOption.label);
            }
            tagCounts++;
          }
        });
        if (!showMore && tagCounts > (props.limitTags || 4)) {
          // Limit Numebr of Chip to avoid overflowing screen, replace with a X More Chip
          tags.pop();
          tags.push(
            <Tooltip
              title={
                <div style={{whiteSpace: 'pre-line', maxHeight: '300px', overflow: 'auto'}}>
                  {moreVehiclesName.join('\n')}
                </div>
              }
              arrow
            >
              <Chip
                size='small'
                key={'tagOverFlow'}
                onClick={() => {
                  setShowMore(true);
                }}
                label={`+${tagCounts - (props.limitTags || 4)} More...`}
              />
            </Tooltip>
          );
        }
        return tags;
      }}
      getLimitTagsText={(more) => {
        return <Chip size='small' key={'more'} label={`+${more} More...`} />;
      }}
      PaperComponent={useCallback((paperProps) => {
        const {children, ...restPaperProps} = paperProps;
        return (
          <Paper {...restPaperProps}>
            {/* Add Box for All/None Buttons */}
            {props.multiple && props.allNoneEnabled && (
              <React.Fragment>
                <Box
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }} // prevent blur
                  sx={{
                    padding: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <button
                    onClick={() => {
                      handleAllRef.current();
                    }}
                    onMouseOut={() => {
                      setHoverAll(false);
                    }}
                    onMouseOver={() => {
                      setHoverAll(true);
                    }}
                    style={{
                      textShadow: 'none',
                      margin: '0 2px',
                      padding: '2px 6px',
                      lineHeight: '14px',
                      fontSize: '10px',
                      borderWidth: '1px',
                      borderRadius: '3px',
                      color: !hoverAll ? '#aaa' : '#62c462',
                      backgroundColor: 'transparent',
                      borderColor: !hoverAll ? '#ccc' : '#62c462',
                    }}
                  >
                    All
                  </button>
                  <button
                    onClick={() => {
                      handleNoneRef.current();
                    }}
                    onMouseOut={() => {
                      setHoverNone(false);
                    }}
                    onMouseOver={() => {
                      setHoverNone(true);
                    }}
                    style={{
                      textShadow: 'none',
                      margin: '0 2px',
                      padding: '2px 6px',
                      lineHeight: '14px',
                      fontSize: '10px',
                      borderWidth: '1px',
                      borderRadius: '3px',
                      color: !hoverNone ? '#aaa' : '#ee5f5b',
                      backgroundColor: 'transparent',
                      borderColor: !hoverNone ? '#ccc' : '#ee5f5b',
                    }}
                  >
                    None
                  </button>
                </Box>
                <Divider />
              </React.Fragment>
            )}
            {children}
          </Paper>
        );
      }, [])}
      isOptionEqualToValue={(option, value) => {
        return option === value || option.value === value;
      }}
    />
  );
}

export function InlineTooltip(props) {
  const placement = props.placement != null ? props.placement : 'bottom-start';
  const tooltipSx = {
    minWidth: props.minWidth || 'auto',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  };
  if (!props.minWidth) {
    tooltipSx.maxWidth = '300px';
  } else {
    tooltipSx.maxWidth = 'min(560px, calc(100vw - 32px))';
  }

  return (
    <Tooltip
      enterTouchDelay={0}
      leaveTouchDelay={10000}
      arrow
      placement={placement}
      PopperProps={{
        disablePortal: true,
        popperOptions: {
          positionFixed: false,
          modifiers: {
            preventOverflow: {
              enabled: true,
              boundary: 'viewport',
              padding: 8,
            },
            flip: {
              enabled: true,
            },
          },
        },
      }}
      sx={{
        display: 'inline-block',
        padding: 0,
      }}
      componentsProps={{
        tooltip: {
          sx: tooltipSx,
        },
      }}
      title={props.title}
    >
      <button className='btn btn-sm d-inline-flex m-0 p-1'>{props.icon}</button>
    </Tooltip>
  );
}

export function SidebarNavTooltip(props) {
  const title = props.title;
  const justifyContent = props.contentJustify || 'center';
  const inner = (
    <Box
      component='span'
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent,
      }}
    >
      {props.children}
    </Box>
  );

  if (props.disableTooltip) {
    return inner;
  }

  return (
    <Tooltip
      enterTouchDelay={0}
      leaveTouchDelay={10000}
      arrow
      placement={props.placement != null ? props.placement : 'right'}
      PopperProps={{
        disablePortal: false,
        style: {zIndex: 10050},
        popperOptions: {
          positionFixed: false,
          modifiers: {
            preventOverflow: {
              enabled: true,
            },
          },
        },
      }}
      sx={{
        display: 'block',
        padding: 0,
        width: '100%',
      }}
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: 280,
            minWidth: props.minWidth || 'auto',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            px: 1.25,
            py: 0.75,
            fontSize: '0.8125rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
            color: '#fff',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
            backgroundColor: '#1e293b',
            border: 'none',
            boxShadow: '0 10px 28px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
          },
        },
        arrow: {
          sx: {
            color: '#1e293b',
            '&::before': {
              border: 'none',
            },
          },
        },
      }}
      title={title}
    >
      {inner}
    </Tooltip>
  );
}
